import React from "react";
import { useBoolean } from "usehooks-ts";
import DepositModal from "./modals/deposit-modal";
import WithdrawlModal from "./modals/withdrawl-modal";
import ExposureLimitModal from "./modals/exposure-limit-modal";
import CreditModal from "./modals/credit-modal";
import PasswordModal from "./modals/password-modal";
import ChangeStatusModal from "./modals/change-status-modal";
import { ITemp } from "./users-table";
import endpoints, { IMember } from "../../../api/endpoints";
import { IUserData } from "../../../redux/types";
import useGetApi from "../../../hooks/use-get-api";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { canAccess } from "../../../utils/permissions";

interface Props {
  rowData: ITemp;
  parentUserId: string;
  isListClients: boolean;
}

const UserRowActionButtons: React.FC<Props> = ({ rowData, parentUserId, isListClients }) => {
  // Single call for the parent user — SWR deduplicates across all rows
  const { data: parentData } = useGetApi<IMember>(
    endpoints.member.getMember(parentUserId)
  );

  const userData = useSelector((state: RootState) => state.user.userData) as IUserData | null;

  const isAdminDownline = userData?.user_id === parentUserId;
  const canDeposit = canAccess(userData, "deposit");
  const canWithdraw = canAccess(userData, "withdraw");
  const canLimit = canAccess(userData, ["bet_lock", "exposure_limit"]);
  const canCreditRef = canAccess(userData, "credit_reference");
  const canChangePassword = canAccess(userData, "user_password_change");
  const canStatus = canAccess(userData, "user_lock");
  const canMore = canAccess(userData, "user_info");

  const openDepositModal = useBoolean(false);
  const openWithdrawlModal = useBoolean(false);
  const openExposureLimitModal = useBoolean(false);
  const openCreditModal = useBoolean(false);
  const openPasswordModal = useBoolean(false);
  const openStatusModal = useBoolean(false);

  return (
    <>
      <div role="group" className="btn-group">
        {canDeposit && (
          <button
            type="button"
            className="btn action-button btn-secondary"
            onClick={() => openDepositModal.toggle()}
          >
            D
          </button>
        )}
        {canWithdraw && (
          <button
            type="button"
            className="btn action-button btn-secondary"
            onClick={() => openWithdrawlModal.toggle()}
          >
            W
          </button>
        )}
        {isAdminDownline && (
          <>
            {canLimit && (
              <button
                type="button"
                className="btn action-button btn-secondary"
                onClick={() => openExposureLimitModal.toggle()}
              >
                L
              </button>
            )}
            {canCreditRef && (
              <button
                type="button"
                className="btn action-button btn-secondary"
                onClick={() => openCreditModal.toggle()}
              >
                C
              </button>
            )}
            {canChangePassword && (
              <button
                type="button"
                className="btn action-button btn-secondary"
                onClick={() => openPasswordModal.toggle()}
              >
                P
              </button>
            )}
            {canStatus && (
              <button
                type="button"
                className="btn action-button btn-secondary"
                onClick={() => openStatusModal.toggle()}
              >
                S
              </button>
            )}
            {!isListClients && canMore && (
              <button
                type="button"
                className="btn action-button btn-secondary"
              >
                More
              </button>
            )}
          </>
        )}
      </div>

      {/* Deposit Modal */}
      {canDeposit && openDepositModal.value && (
        <DepositModal
          parentData={parentData}
          parentUserId={parentUserId}
          rowData={rowData}
          openModal={openDepositModal.value}
          toggleModal={() => openDepositModal.toggle()}
        />
      )}
      {/* WithDrawl Modal */}
      {canWithdraw && openWithdrawlModal.value && (
        <WithdrawlModal
          parentData={parentData}
          parentUserId={parentUserId}
          rowData={rowData}
          openModal={openWithdrawlModal.value}
          toggleModal={() => openWithdrawlModal.toggle()}
        />
      )}
      {/* Exposure Limit Modal */}
      {canLimit && openExposureLimitModal.value && (
        <ExposureLimitModal
          rowData={rowData}
          parentUserId={parentUserId}
          openModal={openExposureLimitModal.value}
          toggleModal={() => openExposureLimitModal.toggle()}
        />
      )}
      {/* Credit Modal */}
      {canCreditRef && openCreditModal.value && (
        <CreditModal
          rowData={rowData}
          parentUserId={parentUserId}
          openModal={openCreditModal.value}
          toggleModal={() => openCreditModal.toggle()}
        />
      )}
      {/* Password */}
      {canChangePassword && openPasswordModal.value && (
        <PasswordModal
          rowData={rowData}
          openModal={openPasswordModal.value}
          toggleModal={openPasswordModal.toggle}
        />
      )}
      {/* Change Status */}
      {canStatus && openStatusModal.value && (
        <ChangeStatusModal
          rowData={rowData}
          parentUserId={parentUserId}
          openModal={openStatusModal.value}
          toggleModal={openStatusModal.toggle}
        />
      )}
    </>
  );
};

export default UserRowActionButtons;
